import { Drawer, IconButton, Typography } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useFormikEnhanced } from '@superdispatch/forms';
import {
  DrawerContent,
  DrawerTitle,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Button, DescriptionLineItem, TextBox } from '@superdispatch/ui-lab';
import { useQueryClient } from '@tanstack/react-query';
import { Form, FormikProvider } from 'formik';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import {
  PastDueInvoice,
  getPastDueTotal,
} from 'shared/modules/subscription/core/PastDueInvoice';
import { MaskedCardNumber } from 'shared/modules/subscription/core/MaskedCardNumber';
import {
  useEstimateTaxPrice,
  useInvalidateSubscriptionQueries,
  useSubscription,
  useSubscriptionAPI,
} from 'shared/modules/subscription/SubscriptionAPI';
import { LinkAnchor } from 'shared/routing/Links';
import { PastDueCardDetailsLocationState } from './SubscriptionPastDueCardDetailsDrawer';

interface SubscriptionPastDueDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function SubscriptionPastDueDrawer({
  open,
  onClose,
}: SubscriptionPastDueDrawerProps) {
  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerTitle
        title="Pay Past Due Balance"
        endAction={
          <IconButton edge="end" aria-label="close" onClick={onClose}>
            <Close />
          </IconButton>
        }
      />
      <PastDueDrawerContent onClose={onClose} />
    </Drawer>
  );
}

interface PastDueDrawerContentProps {
  onClose: () => void;
}

function PastDueDrawerContent({ onClose }: PastDueDrawerContentProps) {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbarStack();
  const { subscription: subscriptionDetails, billableSeats } =
    useSubscription();
  const { collectPaymentPastDue } = useSubscriptionAPI();
  const invalidateSubscriptionQueries = useInvalidateSubscriptionQueries();
  const { data: taxPrice, isLoading } = useEstimateTaxPrice(
    {
      seats_count: billableSeats?.billable_seats,
      include_total_invoice: false,
    },
    { enabled: !!billableSeats?.billable_seats },
  );

  const card = subscriptionDetails?.customer?.payment_method?.card;
  const pastDueInvoices = taxPrice?.past_due_invoices || [];
  const totalAmountDue = getPastDueTotal(pastDueInvoices);

  const formik = useFormikEnhanced({
    initialValues: {},
    onSubmit: () =>
      Promise.all(
        pastDueInvoices.map((invoice) => collectPaymentPastDue(invoice.guid)),
      ),
    onSubmitSuccess: () => {
      invalidateSubscriptionQueries();
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      addSnackbar('Past due balance was paid', { variant: 'success' });
      onClose();
    },
    onSubmitFailure: (error) => {
      addSnackbar(error.message, { variant: 'error' });
    },
  });

  return (
    <DrawerContent>
      {isLoading ? (
        <Stack space="small">
          <Skeleton width={240} />
          <Skeleton />
          <Skeleton />
        </Stack>
      ) : (
        <FormikProvider value={formik}>
          <Form>
            <Stack space="medium">
              <Stack space="xsmall">
                <Typography color="textSecondary" variant="h6">
                  PAYMENT METHOD
                </Typography>
                {card && (
                  <Inline space="xxsmall" verticalAlign="center">
                    <TextBox>
                      <MaskedCardNumber>{card.masked_number}</MaskedCardNumber>
                    </TextBox>
                    <TextBox color="secondary">
                      · Expires {card.expiry_month}/{card.expiry_year}
                    </TextBox>
                    <LinkAnchor
                      to="/settings/subscription/edit_card_details"
                      state={
                        {
                          isFromPastDueDrawer: true,
                        } satisfies PastDueCardDetailsLocationState
                      }
                    >
                      Change
                    </LinkAnchor>
                  </Inline>
                )}
              </Stack>

              <PastDueInvoice pastDueInvoices={pastDueInvoices} />

              <DescriptionLineItem
                title={<TextBox variant="heading-4">Total</TextBox>}
              >
                <TextBox variant="heading-4" align="right">
                  {formatCurrency(totalAmountDue)}
                </TextBox>
              </DescriptionLineItem>

              <Button
                type="submit"
                size="large"
                fullWidth={true}
                pending={formik.isSubmitting}
                disabled={!pastDueInvoices.length}
              >
                Pay {formatCurrency(totalAmountDue)}
              </Button>
            </Stack>
          </Form>
        </FormikProvider>
      )}
    </DrawerContent>
  );
}
